import React, { useState, useEffect } from 'react';
import aiBrainLogo from '../assets/ai-brain-actual.png';
import { getBackendApiUrl } from '../config/backendApi';

interface LoginFormData {
  firmId: string;
  username: string;
  password: string;
}

interface LoginFormProps {
  onLoginSuccess?: (firmId: string, accessToken: string, refreshToken: string) => void;
  onLoginError?: (errorMessage: string) => void;
  isLoading?: boolean;
  setIsLoading?: (loading: boolean) => void;
}

interface PasswordChangeData {
  newPassword: string;
  confirmPassword: string;
}

// Types for recent realm management
interface RecentRealm {
  realmId: string;
  lastUsed: string;
  displayName?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onLoginSuccess,
  onLoginError,
  isLoading: externalLoading = false,
  setIsLoading: setExternalLoading
}) => {
  const [formData, setFormData] = useState<LoginFormData>({
    firmId: '',
    username: '',
    password: ''
  });
  const [internalLoading, setInternalLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Temporary password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [tempAuthData, setTempAuthData] = useState<{
    firmId: string;
    accessToken: string;
    refreshToken: string;
  } | null>(null);
  const [passwordChangeData, setPasswordChangeData] = useState<PasswordChangeData>({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordChangeErrors, setPasswordChangeErrors] = useState<Record<string, string>>({});

  // Enhanced realm ID management state
  const [firmIdFromUrl, setFirmIdFromUrl] = useState(false);
  const [recentRealms, setRecentRealms] = useState<RecentRealm[]>([]);
  const [showRecentRealms, setShowRecentRealms] = useState(false);
  const [lastUsedRealm, setLastUsedRealm] = useState<string | null>(null);

  // Use external loading state if provided, otherwise use internal
  const isLoadingState = setExternalLoading ? externalLoading : internalLoading;
  const setLoadingState = setExternalLoading || setInternalLoading;

  // Enhanced recent realm management
  const RECENT_REALMS_KEY = 'edgelexRecentRealms';
  const MAX_RECENT_REALMS = 5;

  const loadRecentRealms = (): RecentRealm[] => {
    try {
      const stored = localStorage.getItem(RECENT_REALMS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading recent realms:', error);
      return [];
    }
  };

  const saveRecentRealm = (realmId: string) => {
    try {
      const existing = loadRecentRealms();
      const now = new Date().toISOString();

      // Remove if already exists
      const filtered = existing.filter(r => r.realmId !== realmId);

      // Add to front
      const updated = [
        { realmId, lastUsed: now },
        ...filtered
      ].slice(0, MAX_RECENT_REALMS);

      localStorage.setItem(RECENT_REALMS_KEY, JSON.stringify(updated));
      setRecentRealms(updated);

      // Also update the main subscriber ID storage
      localStorage.setItem('webelieveSubscriberId', realmId);
      setLastUsedRealm(realmId);
    } catch (error) {
      console.error('Error saving recent realm:', error);
    }
  };

  const formatRealmDisplayName = (realmId: string): string => {
    // Remove "-realm" suffix for display if present
    return realmId.replace(/-realm$/, '');
  };

  const formatLastUsedTime = (lastUsed: string): string => {
    try {
      const date = new Date(lastUsed);
      const now = new Date();
      const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

      if (diffInHours < 1) return 'Just now';
      if (diffInHours < 24) return `${diffInHours}h ago`;
      if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
      return date.toLocaleDateString();
    } catch {
      return 'Recently';
    }
  };

  // Enhanced auto-population with recent realms support
  useEffect(() => {
    let realmParam: string | null = null;
    let source = '';

    // Load recent realms first
    const recent = loadRecentRealms();
    setRecentRealms(recent);

    // Get the last used realm for display
    const lastUsed = recent.length > 0 ? recent[0].realmId : null;
    setLastUsedRealm(lastUsed);

    // First try URL parameters (direct login links)
    const urlParams = new URLSearchParams(window.location.search);
    realmParam = urlParams.get('realm');
    if (realmParam) {
      source = 'URL parameter';
    } else {
      // Fallback to localStorage (from LoginHandler redirect)
      realmParam = localStorage.getItem('webelieveSubscriberId');
      if (realmParam) {
        source = 'stored from email link';
      } else if (lastUsed) {
        // Use the most recent realm as fallback
        realmParam = lastUsed;
        source = 'last used realm';
      }
    }

    if (realmParam) {
      // Use the full realm parameter as the subscriber ID
      // Users need to enter the complete realm name including "-realm" suffix
      // e.g., "duncanlaw-82d96a15-realm" stays as "duncanlaw-82d96a15-realm"
      const firmId = realmParam;

      console.log(`Auto-populating subscriber ID from ${source}: ${firmId}`);
      setFormData(prev => ({ ...prev, firmId }));
      setFirmIdFromUrl(true);
    }
  }, []);

  // Handle click outside to close recent realms dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showRecentRealms &&
          event.target instanceof Element &&
          !event.target.closest('.recent-realms-container')) {
        setShowRecentRealms(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (showRecentRealms && event.key === 'Escape') {
        setShowRecentRealms(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showRecentRealms]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firmId.trim()) {
      newErrors.firmId = 'Subscriber ID is required';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoadingState(true);
    setErrors({}); // Clear previous errors

    try {
      console.log('Attempting WeBelieve backend authentication with:', { firmId: formData.firmId, username: formData.username });

      // Use the WeBelieve backend API instead of direct Keycloak
      const response = await fetch(getBackendApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscriberId: formData.firmId,
          username: formData.username,
          password: formData.password
        })
      });

      console.log('WeBelieve backend response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('WeBelieve backend error:', errorData);

        let message = 'Login failed';
        if (response.status === 401) {
          message = errorData.message || 'Invalid username or password';
        } else if (response.status === 404) {
          message = 'Subscriber not found. Please check your Subscriber ID.';
        } else if (errorData.message) {
          message = errorData.message;
        }

        throw new Error(message);
      }

      const result = await response.json();
      console.log('WeBelieve backend authentication successful');

      if (result.success && result.accessToken && result.refreshToken) {
        // Save the successful login realm to recent realms
        saveRecentRealm(formData.firmId);

        // Check if user has temporary password and needs to change it
        if (result.requiresPasswordChange || result.temporaryPassword) {
          console.log('User has temporary password, showing password change modal');
          setTempAuthData({
            firmId: formData.firmId,
            accessToken: result.accessToken,
            refreshToken: result.refreshToken
          });
          setShowPasswordChange(true);
          return; // Don't call onLoginSuccess yet
        }

        // Normal login flow - call success callback if provided
        if (onLoginSuccess) {
          onLoginSuccess(formData.firmId, result.accessToken, result.refreshToken);
        } else {
          // Fallback: store tokens and redirect (legacy behavior)
          localStorage.setItem('webelieveSubscriberId', formData.firmId);
          localStorage.setItem('webelieve_access_token', result.accessToken);
          localStorage.setItem('webelieve_refresh_token', result.refreshToken);
          window.location.href = 'https://app.webelievesoftware.com/';
        }
      } else {
        throw new Error('Invalid response from authentication service');
      }

    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Login failed. Please try again.';

      if (onLoginError) {
        onLoginError(errorMessage);
      } else {
        // Fallback: set local error state
        setErrors({ submit: errorMessage });
      }
    } finally {
      setLoadingState(false);
    }
  };

  const updateFormData = (field: keyof LoginFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleRecentRealmSelect = (realmId: string) => {
    setFormData(prev => ({ ...prev, firmId: realmId }));
    setShowRecentRealms(false);
    setErrors(prev => ({ ...prev, firmId: '' }));
  };

  const clearRecentRealms = () => {
    localStorage.removeItem(RECENT_REALMS_KEY);
    localStorage.removeItem('webelieveSubscriberId');
    setRecentRealms([]);
    setLastUsedRealm(null);
    setFormData(prev => ({ ...prev, firmId: '' }));
    setShowRecentRealms(false);
  };

  const validatePasswordChange = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!passwordChangeData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordChangeData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setPasswordChangeErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordChange() || !tempAuthData) return;

    setLoadingState(true);
    setPasswordChangeErrors({});

    try {
      console.log('Changing temporary password...');

      const response = await fetch(getBackendApiUrl('/api/auth/change-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscriberId: tempAuthData.firmId,
          accessToken: tempAuthData.accessToken,
          newPassword: passwordChangeData.newPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Password change failed');
      }

      const result = await response.json();
      console.log('Password changed successfully');

      if (result.success && result.accessToken && result.refreshToken) {
        // Password change successful, now complete the login
        setShowPasswordChange(false);
        setTempAuthData(null);

        if (onLoginSuccess) {
          onLoginSuccess(tempAuthData.firmId, result.accessToken, result.refreshToken);
        } else {
          // Fallback: store tokens and redirect
          localStorage.setItem('webelieveSubscriberId', tempAuthData.firmId);
          localStorage.setItem('webelieve_access_token', result.accessToken);
          localStorage.setItem('webelieve_refresh_token', result.refreshToken);
          window.location.href = 'https://app.webelievesoftware.com/';
        }
      } else {
        throw new Error('Invalid response from password change service');
      }

    } catch (error) {
      console.error('Password change error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Password change failed. Please try again.';
      setPasswordChangeErrors({ submit: errorMessage });
    } finally {
      setLoadingState(false);
    }
  };

  const updatePasswordChangeData = (field: keyof PasswordChangeData, value: string) => {
    setPasswordChangeData(prev => ({ ...prev, [field]: value }));
    if (passwordChangeErrors[field]) {
      setPasswordChangeErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-600/80 to-slate-950 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
      <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500/15 rounded-full blur-3xl animate-pulse delay-1000"></div>

      <div className="relative max-w-md w-full bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg ring-4 ring-blue-500/30 overflow-hidden">
            <img
              src={aiBrainLogo}
              alt="WeBelieve Logo"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Sign In to WeBelieve
          </h1>
          <p className="text-blue-300">
            Access your AI-powered business workspace
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          {/* Subscriber ID */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-blue-300">Subscriber ID</label>
              {lastUsedRealm && !firmIdFromUrl && (
                <span className="text-xs text-blue-300 bg-blue-500/20 px-2 py-1 rounded-full">
                  Last used: {formatRealmDisplayName(lastUsedRealm)}
                </span>
              )}
            </div>
            <div className="relative recent-realms-container">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <input
                type="text"
                value={formData.firmId}
                onChange={(e) => updateFormData('firmId', e.target.value)}
                className={`w-full pl-10 pr-12 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                  errors.firmId ? 'border-red-400' : 'border-white/20'
                } ${firmIdFromUrl ? 'bg-white/5 cursor-not-allowed' : ''}`}
                placeholder="e.g., my-subscriber"
                required
                readOnly={firmIdFromUrl}
              />
              {/* Recent Realms Dropdown Button */}
              {!firmIdFromUrl && recentRealms.length > 0 && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <button
                    type="button"
                    onClick={() => setShowRecentRealms(!showRecentRealms)}
                    className="text-blue-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 transition-colors"
                    title="Show recent subscriber IDs"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Recent Realms Dropdown */}
              {showRecentRealms && !firmIdFromUrl && (
                <div className="absolute z-10 w-full mt-1 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg shadow-lg">
                  <div className="p-2 border-b border-white/20">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-300">Recent Subscriber IDs</span>
                      <button
                        type="button"
                        onClick={clearRecentRealms}
                        className="text-xs text-blue-300 hover:text-red-400 transition-colors"
                        title="Clear recent subscriber IDs"
                      >
                        Clear all
                      </button>
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {recentRealms.map((realm) => (
                      <button
                        key={realm.realmId}
                        type="button"
                        onClick={() => handleRecentRealmSelect(realm.realmId)}
                        className="w-full px-3 py-2 text-left hover:bg-white/10 focus:bg-white/10 focus:outline-none transition-colors group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white truncate">
                              {formatRealmDisplayName(realm.realmId)}
                            </div>
                            <div className="text-xs text-blue-300 truncate">
                              {realm.realmId}
                            </div>
                          </div>
                          <div className="ml-2 text-xs text-blue-400 group-hover:text-blue-300">
                            {formatLastUsedTime(realm.lastUsed)}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {errors.firmId && <p className="mt-1 text-sm text-red-400">{errors.firmId}</p>}
            {firmIdFromUrl && <p className="mt-1 text-sm text-green-300">✓ Subscriber ID auto-detected from your login link</p>}
            {lastUsedRealm && !firmIdFromUrl && formData.firmId === lastUsedRealm && (
              <p className="mt-1 text-sm text-green-300">✓ Using your last used subscriber ID</p>
            )}
          </div>

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-blue-300 mb-2">Username</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => updateFormData('username', e.target.value)}
                className={`w-full pl-10 pr-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                  errors.username ? 'border-red-400' : 'border-white/20'
                }`}
                placeholder="your.email.com"
                required
              />
            </div>
            {errors.username && <p className="mt-1 text-sm text-red-400">{errors.username}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-blue-300 mb-2">Password</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => updateFormData('password', e.target.value)}
                className={`w-full pl-10 pr-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                  errors.password ? 'border-red-400' : 'border-white/20'
                }`}
                placeholder="Enter your password"
                required
              />
            </div>
            {errors.password && <p className="mt-1 text-sm text-red-400">{errors.password}</p>}
          </div>

          <button
            type="submit"
            disabled={isLoadingState}
            className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-slate-600 disabled:to-slate-700 text-white font-semibold rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isLoadingState && (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isLoadingState ? 'Signing In...' : 'Sign In to WeBelieve'}
          </button>

          {errors.submit && (
            <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-4">
              <p className="text-red-200 text-sm">{errors.submit}</p>
            </div>
          )}
        </form>

        <div className="mt-8 text-center space-y-4">
          <button
            onClick={() => window.location.href = '/PasswordReset'}
            className="text-blue-300 hover:text-white text-sm font-medium transition-colors"
          >
            Forgot your password?
          </button>

          <div className="border-t border-white/20 pt-4">
            <p className="text-blue-300 text-sm">
              Need to create a new subscriber account?{' '}
              <button
                onClick={() => window.location.href = '/signup'}
                className="text-blue-400 hover:underline font-medium"
              >
                Sign Up
              </button>
            </p>
          </div>

          <p className="text-blue-300 text-sm">
            Need help? Contact{' '}
            <a href="mailto:support@webelievesoftware.com" className="text-blue-400 hover:underline">
              support@webelievesoftware.com
            </a>
          </p>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4 z-50">
          <div className="relative max-w-md w-full bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Change Your Password
              </h1>
              <p className="text-blue-300">
                You're using a temporary password. Please set a new password to continue.
              </p>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-6">
              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-blue-300 mb-2">New Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type="password"
                    value={passwordChangeData.newPassword}
                    onChange={(e) => updatePasswordChangeData('newPassword', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                      passwordChangeErrors.newPassword ? 'border-red-400' : 'border-white/20'
                    }`}
                    placeholder="Enter new password (min 8 chars)"
                    required
                  />
                </div>
                {passwordChangeErrors.newPassword && <p className="mt-1 text-sm text-red-400">{passwordChangeErrors.newPassword}</p>}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-blue-300 mb-2">Confirm New Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <input
                    type="password"
                    value={passwordChangeData.confirmPassword}
                    onChange={(e) => updatePasswordChangeData('confirmPassword', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                      passwordChangeErrors.confirmPassword ? 'border-red-400' : 'border-white/20'
                    }`}
                    placeholder="Confirm new password"
                    required
                  />
                </div>
                {passwordChangeErrors.confirmPassword && <p className="mt-1 text-sm text-red-400">{passwordChangeErrors.confirmPassword}</p>}
              </div>

              <button
                type="submit"
                disabled={isLoadingState}
                className="w-full py-3 px-6 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-slate-600 disabled:to-slate-700 text-white font-semibold rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isLoadingState && (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isLoadingState ? 'Updating Password...' : 'Update Password'}
              </button>

              {passwordChangeErrors.submit && (
                <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-4">
                  <p className="text-red-200 text-sm">{passwordChangeErrors.submit}</p>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
